// components
import {
  LocalizationProvider,
  MultiInputDateRangeField
} from '@mui/x-date-pickers-pro';
import type {
  DateRangeValidationError,
  PickerChangeHandlerContext
} from '@mui/x-date-pickers-pro';

// styles
import { StyledDateRangePicker } from './ControlledDateRangePicker.styles';

// types
import type { ControlledDateRangePickerProps } from './ControlledDateRangePicker.types';
import type { PickerRangeValue } from '@mui/x-date-pickers/internals/models/value';
import type { FieldOwnerState } from '@mui/x-date-pickers/models';

// utils
import { useController } from 'react-hook-form';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

export default function ControlledDateRangePicker({
  control,
  startName,
  endName,
  startLabel,
  endLabel,
  disabled,
  minDate,
  maxDate
}: ControlledDateRangePickerProps) {
  'use memo';

  const { field: startField, fieldState: startState } = useController({
    name: startName,
    control
  });
  const { field: endField, fieldState: endState } = useController({
    name: endName,
    control
  });

  const value: PickerRangeValue = [
    startField.value && dayjs(startField.value).isValid()
      ? dayjs(startField.value)
      : null,
    endField.value && dayjs(endField.value).isValid()
      ? dayjs(endField.value)
      : null
  ];

  const handleChange = (
    range: PickerRangeValue,
    _context: PickerChangeHandlerContext<DateRangeValidationError>
  ) => {
    const [start, end] = range;
    startField.onChange(start ? dayjs(start).toDate() : null);
    endField.onChange(end ? dayjs(end).toDate() : null);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nl">
      <StyledDateRangePicker
        value={value}
        onChange={handleChange}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        slots={{ field: MultiInputDateRangeField }}
        slotProps={{
          textField: ({
            position
          }: FieldOwnerState & { position?: 'start' | 'end' }) => ({
            label: position === 'start' ? startLabel : endLabel,
            size: 'small',
            error: position === 'start' ? !!startState.error : !!endState.error,
            helperText:
              position === 'start'
                ? startState.error?.message
                : endState.error?.message
          })
        }}
      />
    </LocalizationProvider>
  );
}
